// The live agent/Knowledge-Bank graph.
// Backend: GET /api/admin/agent-console/topology (services/agent_console/topology.py).
// Every shape here mirrors a Pydantic model on that endpoint.

import api from './api';

export type AgentKind = 'picker' | 'sub_profile' | 'headless' | 'door';
export type BankKind = 'playbook' | 'domain';
export type MountAccess = 'read+write' | 'read-only';
export type EntryPointKind = 'console' | 'door' | 'schedule' | 'pipeline';

/** One way an agent is actually reached — a door, the console, a schedule. */
export interface EntryPoint {
  kind: EntryPointKind;
  label: string;
  /** A URL prefix, a cron trigger, or the pipeline that calls it. */
  detail: string;
  /** Repo-relative path of the wiring, for click-through. */
  source: string;
}

export interface BankMount {
  bank: string;
  label: string;
  /** Mounted MCP server name; its tools are `mcp__<mount>__*`. */
  mount: string;
  access: MountAccess;
  kind: BankKind;
  /** True when an admin override moved this mount away from the code baseline. */
  overridden?: boolean;
  /** The code-declared access when overridden ('read+write'/'read-only'/'absent'). */
  code_access?: string | null;
}

/** What an admin may set an (agent, bank) pair to — the API's vocabulary. */
export type OverrideAccess = 'read_write' | 'read_only' | 'removed';

/** One stored admin override on an agent, including removals. */
export interface MountOverrideRef {
  bank: string;
  label: string;
  /** 'read+write' / 'read-only' / 'removed'. */
  access: string;
  /** 'read+write' / 'read-only' / 'absent' — what the code declares. */
  code_access: string;
  kind: BankKind;
}

/** A bank an admin may mount on an agent — the add-mount picker's catalog. */
export interface MountableBank {
  id: string;
  label: string;
  kind: BankKind;
}

export interface FieldOverride {
  field: string;
  parent: unknown;
  value: unknown;
}

export interface Inheritance {
  parent: string | null;
  inherited: string[];
  overridden: FieldOverride[];
}

export interface AccessRef {
  id: string;
  label: string;
}

export interface AgentNode {
  id: string;
  name: string;
  description: string;
  kind: AgentKind;
  kind_note: string;
  prompt_kind: string;
  model: string | null;
  writes: boolean;
  reskinned: boolean;
  is_merlin: boolean;
  tools: string[];
  access: AccessRef[];
  banks: BankMount[];
  /** Stored admin overrides on this agent, INCLUDING removals. */
  mount_overrides?: MountOverrideRef[];
  inheritance: Inheritance;
  entry_points: EntryPoint[];
}

export interface BankNode {
  id: string;
  label: string;
  kind: BankKind;
  provisioned: boolean;
  /** Agent Console agents only — another agent stack may also reach the bank. */
  readers: string[];
  writers: string[];
}

export interface SurfaceNode {
  id: string;
  label: string;
  is_agent: boolean;
  /** The agent behind the window, or null when nothing runs the tag. */
  runs: string | null;
  retired: boolean;
}

export interface AgentTopology {
  agents: AgentNode[];
  banks: BankNode[];
  surfaces: SurfaceNode[];
  /** Every bank an admin may mount on an agent — the add-mount picker. */
  mountable_banks?: MountableBank[];
}

export async function fetchAgentTopology(): Promise<AgentTopology> {
  const { data } = await api.get<AgentTopology>('/admin/agent-console/topology');
  return data;
}

/**
 * Set one agent↔bank override. The change is REAL — the agent's next turn
 * mounts it — and the response is the updated graph, so the caller re-renders
 * from what the server now believes rather than an optimistic guess.
 */
export async function putMountOverride(
  profileId: string,
  bankId: string,
  access: OverrideAccess,
): Promise<AgentTopology> {
  const { data } = await api.put<AgentTopology>(
    '/admin/agent-console/topology/overrides',
    { profile_id: profileId, bank_id: bankId, access },
  );
  return data;
}

/** Clear one override — the pair reverts to what the code declares. */
export async function deleteMountOverride(
  profileId: string,
  bankId: string,
): Promise<AgentTopology> {
  const { data } = await api.delete<AgentTopology>(
    `/admin/agent-console/topology/overrides/${encodeURIComponent(profileId)}/${bankId}`,
  );
  return data;
}

/**
 * Merlin-scoped view: the front-of-house agents, plus anything they reach.
 *
 * Scott, on the graph: "the only ones I care about here are the Merlin agents."
 * Filtering agents alone would leave dangling bank nodes, so the banks and
 * surfaces are narrowed to the ones still connected to a surviving agent.
 */
export function filterToMerlin(topology: AgentTopology): AgentTopology {
  const agents = topology.agents.filter((a) => a.is_merlin);
  const kept = new Set(agents.map((a) => a.id));
  const reachable = new Set(agents.flatMap((a) => a.banks.map((b) => b.bank)));
  return {
    agents,
    banks: topology.banks
      .filter((b) => reachable.has(b.id))
      .map((b) => ({
        ...b,
        readers: b.readers.filter((id) => kept.has(id)),
        writers: b.writers.filter((id) => kept.has(id)),
      })),
    surfaces: topology.surfaces.filter((s) => s.runs !== null && kept.has(s.runs)),
    mountable_banks: topology.mountable_banks,
  };
}

/** Total distinct write edges — the number worth watching on this page. */
export function countWriteEdges(topology: AgentTopology): number {
  return topology.agents.reduce(
    (n, a) => n + a.banks.filter((b) => b.access === 'read+write').length,
    0,
  );
}
